/**
 * Chapter replay levels
 * ([Gamification](../../docs/GAMIFICATION.md#chapter-replay-levels),
 * [Review](../../docs/REVIEW.md#chapter-replay-levels)).
 *
 * A finished chapter can be played again, harder. It is the cheapest depth in
 * the whole plan — four passes over content that is already authored — and the
 * only feature that gives a child who *likes* a topic a reason to stay with it.
 *
 * **A replay is a band floor, not a second engine.** The five bands in
 * [`difficulty`](./difficulty.ts) already *are* the scaffolding ladder: word
 * banks thin, options increase, L1 hides, replays run out, speech speeds up.
 * "Replay with less scaffolding" therefore needs no new rules — it needs the
 * same lesson built at a band the child would not otherwise be on. Anything
 * else would be a second, parallel definition of "hard", and the two would
 * drift.
 *
 * **Nothing here is ever required.** The next chapter unlocks at one pass; the
 * three above it are optional forever, do not expire, and a child who never
 * touches them is not behind ([D-009](../../docs/DECISIONS.md#d-009)).
 */
/**
 * A pass over a chapter. `0` is the ordinary first time through.
 *
 * Four of them, matching the four filled rings. The doc arithmetic elsewhere
 * that says "5 levels" is counting `○` — the empty ring is not a pass.
 */
export const REPLAY_LEVELS = [0, 1, 2, 3];
/** The top of the ladder. A chapter played at this level is Mastered. */
export const MASTERED_LEVEL = 3;
/** The rings, by how many passes are finished. Index 0 is "not started". */
export const RINGS = ['○', '◔', '◑', '◕', '●'];
const RULES = {
    0: {
        level: 0,
        nameL1: 'First time through',
        ring: RINGS[1],
        minBand: 1,
        speakingFirst: false,
    },
    1: {
        level: 1,
        nameL1: 'Fewer hints',
        ring: RINGS[2],
        minBand: 3,
        speakingFirst: false,
    },
    2: {
        level: 2,
        nameL1: 'Speaking focus',
        ring: RINGS[3],
        minBand: 4,
        speakingFirst: true,
    },
    3: {
        level: 3,
        nameL1: 'Mastered',
        ring: RINGS[4],
        minBand: 5,
        speakingFirst: true,
    },
};
/** True for a number that is one of the four passes. */
export const isReplayLevel = (level) => REPLAY_LEVELS.includes(level);
/**
 * The rules for one pass.
 *
 * Anything that is not a level answers with the first pass rather than
 * throwing: a stored `replay_level` that has gone strange, or a client asking
 * for level 9, should get a child an ordinary lesson, not an error screen.
 */
export const rulesFor = (level) => RULES[isReplayLevel(level) ? level : 0];
/**
 * The band to build a replay at: the higher of what the child is on and what
 * the pass demands.
 */
export const bandForReplay = (band, level) => Math.max(band, rulesFor(level).minBand);
/** The ring for a number of finished passes. Out of range clamps rather than throws. */
export const ringFor = (passes) => {
    const at = Math.min(Math.max(Math.trunc(passes), 0), RINGS.length - 1);
    // `slice` rather than an index. The clamp already makes `at` valid, so an
    // index would need a `?? RINGS[0]` fallback that no input can reach — a
    // branch written only to satisfy the type checker and impossible to test.
    return RINGS.slice(at, at + 1).join('');
};
/**
 * How many passes a chapter has finished, from its lessons.
 *
 * Each entry is the highest level that lesson has been completed at, or `null`
 * for a lesson never finished. The chapter is at the level its **weakest**
 * lesson is at: a chapter is not "replayed with fewer hints" when five of its
 * six lessons were. That makes the ring mean something, and it is the reason
 * this is a fold over lessons rather than a counter on the chapter that
 * something has to remember to bump.
 *
 * A chapter with no lessons is at zero. That is a content bug rather than an
 * achievement, and answering "mastered" to `min` over nothing is how a linter
 * hole becomes a badge.
 */
export function passesDone(perLesson) {
    if (perLesson.length === 0)
        return 0;
    let lowest = RINGS.length - 1;
    for (const level of perLesson) {
        const passes = level === null ? 0 : Math.min(level + 1, RINGS.length - 1);
        if (passes < lowest)
            lowest = passes;
    }
    return lowest;
}
export function nextReplay(passes) {
    if (passes <= 0)
        return { kind: 'finish-first' };
    if (passes >= RINGS.length - 1)
        return { kind: 'mastered' };
    // One pass done means level 1 is next: the levels and the passes are offset
    // by exactly the first pass, which is level 0.
    return { kind: 'replay', rules: rulesFor(passes) };
}
//# sourceMappingURL=replay.js.map